<template>
  <section id="sobre" class="section-shell about-section" data-theme="grid">
    <div class="section-header" data-reveal>
      <h2>
        A <span class="word-brand">ZIMA</span> é um sistema de
        <span class="typing-slot">
          <span class="typing-word" aria-label="leitura, decisão e execução">{{ typedWord }}</span>
          <span class="typing-cursor" aria-hidden="true">|</span>
        </span>
        <span class="after-typing fixed-line">no mercado cripto.</span>
      </h2>
      <p class="intro">
        <span class="intro-line intro-top">
          Um sistema completo de análise cripto onde você recebe
          <span class="intro-caps">leitura macro, setups, mapas institucionais e estratégia</span>
        </span>
        <span class="intro-line intro-sub">&mdash; tudo traduzido para execução real.</span>
      </p>
    </div>

    <div class="about-grid">
      <div class="card" data-reveal>
        <p class="card-title">Um ambiente criado para quem quer:</p>
        <ul class="bullets">
          <li v-for="item in forWho" :key="item">{{ item }}</li>
        </ul>
      </div>

      <div class="card focus" data-reveal>
        <p class="closing">
          <span class="closing-line closing-marker"><span class="closing-brand">Aqui, o mercado deixa de ser aleatório</span></span>
          <span class="closing-line closing-emphasis">&mdash; e passa a fazer sentido.</span>
        </p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';

const forWho = [
  'clareza em um mercado ruidoso',
  'visão estratégica real',
  'entender a intenção institucional',
  'antecipar movimentos, não reagir a eles',
  'construir independência operacional',
];

const words = ['leitura', 'decisão', 'execução'];
const typedWord = ref('');
let wordIndex = 0;
let charIndex = 0;
let deleting = false;
let typingTimer = null;

const scheduleNext = (delay) => {
  typingTimer = window.setTimeout(stepTyping, delay);
};

const stepTyping = () => {
  const current = words[wordIndex];

  if (!deleting) {
    charIndex += 1;
    typedWord.value = current.slice(0, charIndex);

    if (charIndex >= current.length) {
      deleting = true;
      scheduleNext(1600);
      return;
    }

    scheduleNext(130);
    return;
  }

  charIndex -= 1;
  typedWord.value = current.slice(0, Math.max(0, charIndex));

  if (charIndex <= 0) {
    deleting = false;
    wordIndex = (wordIndex + 1) % words.length;
    scheduleNext(360);
    return;
  }

  scheduleNext(90);
};

onMounted(() => {
  stepTyping();
});

onBeforeUnmount(() => {
  if (typingTimer) {
    window.clearTimeout(typingTimer);
    typingTimer = null;
  }
});
</script>

<style scoped>
.about-section {
  background: linear-gradient(145deg, rgba(11, 13, 20, 0.96), rgba(16, 22, 36, 0.92));
  border: 1px solid rgba(115, 148, 240, 0.14);
  position: relative;
  border-radius: 20px;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
}

.about-section,
.about-section * {
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif !important;
}

.section-header h2 {
  font-size: clamp(30px, 3.2vw, 44px);
  line-height: 1.08;
  color: #eef4ff;
  margin: 4px 0 10px;
  letter-spacing: -0.02em;
  text-shadow:
    0 1px 0 rgba(255, 255, 255, 0.08),
    0 10px 24px rgba(8, 14, 30, 0.34);
}

.word-brand {
  background: linear-gradient(120deg, #6fa6ff 0%, #bd8bff 45%, #7ea5ff 75%, #d2b2ff 100%);
  background-size: 220% 220%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: 0 0 14px rgba(127, 166, 255, 0.28);
  animation: zima-brand-shift 4.2s ease-in-out infinite;
  will-change: background-position;
}

.typing-slot {
  display: inline-flex;
  align-items: baseline;
  width: auto;
  min-width: 0;
  max-width: none;
  margin-right: 0.18ch;
  vertical-align: baseline;
}

.typing-word {
  display: inline-block;
  width: auto;
  white-space: nowrap;
  background: linear-gradient(120deg, #6fa6ff 0%, #bd8bff 45%, #7ea5ff 75%, #d2b2ff 100%);
  background-size: 220% 220%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: 0 0 14px rgba(127, 166, 255, 0.28);
  animation: zima-brand-shift 4.2s ease-in-out infinite;
  will-change: background-position;
}

.typing-cursor {
  display: inline-block;
  margin-left: 0.02ch;
  color: #8eaefb;
  text-shadow: 0 0 10px rgba(127, 166, 255, 0.32);
  animation: about-cursor 0.72s steps(1, end) infinite;
}

.after-typing {
  display: inline-block;
  white-space: nowrap;
}

.after-typing.fixed-line {
  display: block;
}

.intro {
  color: #d7e1ff;
  font-size: 16px;
  line-height: 1.55;
  max-width: 70ch;
}

.intro-line {
  display: block;
}

.intro-top {
  font-size: clamp(0.9rem, 1.05vw, 1.05rem);
  letter-spacing: -0.01em;
  text-wrap: balance;
}

.intro-caps {
  text-transform: uppercase;
  letter-spacing: 0.005em;
  font-weight: 700;
  color: #94d2bd;
}

.intro-sub {
  font-style: italic;
}

.about-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.card {
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 10px 14px;
  position: relative;
  overflow: visible;
}

.card::before {
  content: none;
}

.card-title {
  font-weight: 700;
  color: var(--zima-foreground);
  font-size: clamp(1.22rem, 1.75vw, 1.52rem);
  margin-bottom: 10px;
  position: relative;
  z-index: 1;
}

.bullets {
  list-style: none;
  padding-left: 0;
  display: grid;
  gap: 8px;
  position: relative;
  z-index: 1;
}

.bullets li {
  position: relative;
  padding-left: 12px;
  font-size: clamp(1rem, 1.22vw, 1.1rem);
  line-height: 1.3;
  font-style: italic;
  color: var(--zima-muted);
}

.bullets li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.2em;
  width: 2px;
  height: 1.05em;
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(143, 187, 255, 0.95), rgba(190, 145, 255, 0.9));
  box-shadow: 0 0 10px rgba(143, 187, 255, 0.35);
}

.closing {
  width: 100%;
  font-size: clamp(1.55rem, 2.4vw, 2.2rem);
  line-height: 1.16;
  color: var(--zima-foreground);
  font-weight: 700;
  position: relative;
  z-index: 1;
  margin: 0;
}

.closing-line {
  display: block;
}

.closing-line.closing-marker {
  position: relative;
  padding-left: 12px;
  margin-top: 4px;
}

.closing-line.closing-marker::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.2em;
  width: 2px;
  height: calc(100% + 0.01em);
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(143, 187, 255, 0.95), rgba(190, 145, 255, 0.9));
  box-shadow: 0 0 10px rgba(143, 187, 255, 0.35);
}

.closing-brand {
  background: linear-gradient(135deg, #4e73df 0%, #9b59b6 100%);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  text-shadow: 0 2px 12px rgba(78, 115, 223, 0.35);
}

.closing-emphasis {
  margin-top: 8px;
  font-style: italic;
}

.focus {
  background: transparent;
  display: flex;
  align-items: center;
  min-height: 0;
}

@keyframes zima-brand-shift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

@keyframes about-cursor {
  0%, 45% { opacity: 1; }
  46%, 100% { opacity: 0; }
}

@media (max-width: 1024px) {
  .intro-top {
    white-space: normal;
  }

  .focus {
    min-height: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .word-brand,
  .typing-word,
  .typing-cursor {
    animation: none !important;
  }
}
</style>
